import { useCallback } from "react";

import { AcknowledgeNoticeRequest, NoticePageContext } from "src/api/Tmd.dtos";
import type { ILayoutItemContext } from "src/contexts/layouts/LayoutContext";
import useServiceStackClient from "src/hooks/api/useServiceStackClient";

/**
 * Creates a callback that acknowledges a notice with the captured page context.
 *
 * Preconditions: A service-client provider is available; callers supply the request arguments.
 * Postconditions: Hook evaluation sends nothing. Invocation returns the response promise or
 * rejection; this hook does not refresh caches, navigate or normalize payload values.
 * Assumptions: The endpoint owns authorization and validation; callback identity follows its
 * listed dependencies.
 * Page context is included when any supported ID is not undefined, including zero or null.
 */
const useAcknowledgeNotice = (pageContext: ILayoutItemContext) => {
    const client = useServiceStackClient();
    const { groupId, trialId, institutionId, trialInstitutionId, patientId, personnelId } =
        pageContext;
    const hasPageContext =
        groupId !== undefined ||
        trialId !== undefined ||
        institutionId !== undefined ||
        trialInstitutionId !== undefined ||
        patientId !== undefined ||
        personnelId !== undefined;

    return useCallback(
        async (noticeId: number) =>
            client.post(
                new AcknowledgeNoticeRequest({
                    noticeId,
                    pageContext: hasPageContext
                        ? new NoticePageContext({
                              groupId,
                              trialId,
                              institutionId,
                              trialInstitutionId,
                              patientId,
                              personnelId
                          })
                        : undefined
                })
            ),
        [
            client,
            groupId,
            trialId,
            institutionId,
            trialInstitutionId,
            patientId,
            personnelId,
            hasPageContext
        ]
    );
};

export default useAcknowledgeNotice;
